import type { BlockType, CourseBlock, ListStyle } from './blocks';

export type AuthoringBlockType =
  | 'basic-content-a'
  | 'video'
  | 'stylized-numbered-list'
  | 'click-expand'
  | 'stylized-sidebar-callout'
  | 'stylized-text-callout'
  | 'comparison-slider'
  | 'workplace-color-spectrum'
  | 'likert-scale'
  | 'policy-acknowledgement'
  | 'electronic-signature'
  | 'map'
  | 'categorization'
  | 'single-choice-answer'
  | 'freeform-text'
  | 'lesson-complete'
  | 'link';

export interface AuthoringBlockTypeOption {
  type: AuthoringBlockType;
  chooserLabel: string;
}

export interface BlockTypeMetadata {
  label: string;
  chooserLabel?: string;
  supportsInteractionSettings?: boolean;
  supportsSuccessAnimationSettings?: boolean;
  supportsHideResults?: boolean;
  /**
   * NA-444: Freeform Text has no results view at all, so "Hide results" is
   * forced on and locked (not an author-optional toggle like
   * `supportsHideResults`). Distinct flag because the checkbox still renders
   * — just checked and disabled — rather than being omitted entirely.
   */
  forcesHideResults?: boolean;
  completionMode?: 'none' | 'scroll';
  titleStrategy?: 'none' | 'plain' | 'rich';
  defaultRequireInteraction?: boolean;
  alwaysRequiresInteraction?: boolean;
  defaultInteractionMessage?: string;
}

export type BlockCompletionMode = 'none' | 'scroll' | 'interaction';
export type BlockTitleStrategy = 'none' | 'plain' | 'rich';

const BLOCK_TYPE_METADATA: Record<BlockType, BlockTypeMetadata> = {
  video: {
    label: 'Video',
    chooserLabel: 'Video',
    completionMode: 'scroll',
    titleStrategy: 'plain',
    supportsInteractionSettings: true,
    defaultRequireInteraction: true,
    defaultInteractionMessage: 'Please watch this video to continue.',
  },
  'basic-content-a': {
    label: 'Content Block',
    chooserLabel: 'Basic Content Block',
    completionMode: 'scroll',
    titleStrategy: 'plain',
  },
  'basic-content-b': {
    label: 'Content Block',
    completionMode: 'scroll',
    titleStrategy: 'plain',
  },
  'stylized-text-callout': {
    label: 'Text Callout',
    chooserLabel: 'Text Callout',
    completionMode: 'scroll',
    titleStrategy: 'none',
  },
  'stylized-numbered-list': {
    label: 'Numbered List',
    chooserLabel: 'List',
    completionMode: 'scroll',
    titleStrategy: 'plain',
  },
  'stylized-sidebar-callout': {
    label: 'Text with Sidebar',
    chooserLabel: 'Text with Sidebar',
    completionMode: 'scroll',
    titleStrategy: 'rich',
  },
  'comparison-slider': {
    label: 'Comparison Slider',
    chooserLabel: 'Comparison',
    completionMode: 'scroll',
    titleStrategy: 'rich',
    supportsInteractionSettings: true,
    defaultRequireInteraction: false,
    defaultInteractionMessage: 'Please view both options to continue.',
  },
  'click-expand': {
    label: 'Click Expand',
    chooserLabel: 'Click-Expand',
    completionMode: 'scroll',
    titleStrategy: 'plain',
    supportsInteractionSettings: true,
    defaultRequireInteraction: false,
    defaultInteractionMessage: 'Please view all topics to continue.',
  },
  'workplace-color-spectrum': {
    label: 'Workplace Color Spectrum',
    chooserLabel: 'Workplace Color Spectrum',
    completionMode: 'none',
    titleStrategy: 'plain',
    defaultRequireInteraction: true,
    alwaysRequiresInteraction: true,
    supportsHideResults: true,
  },
  'likert-scale': {
    label: 'Likert Scale',
    chooserLabel: 'Likert Scale',
    completionMode: 'none',
    titleStrategy: 'plain',
    defaultRequireInteraction: true,
    alwaysRequiresInteraction: true,
    supportsHideResults: true,
  },
  'policy-acknowledgement': {
    label: 'Policy Acknowledgement',
    chooserLabel: 'Policy Acknowledgement',
    completionMode: 'none',
    titleStrategy: 'plain',
    supportsInteractionSettings: true,
    defaultRequireInteraction: true,
    defaultInteractionMessage: 'You must review and acknowledge the document before continuing.',
  },
  'electronic-signature': {
    label: 'Electronic Signature',
    chooserLabel: 'Electronic Signature',
    completionMode: 'none',
    titleStrategy: 'plain',
    supportsInteractionSettings: true,
    defaultRequireInteraction: true,
    defaultInteractionMessage: 'You must acknowledge that you have completed this course before continuing.',
  },
  map: {
    label: 'Map',
    chooserLabel: 'Map',
    completionMode: 'none',
    titleStrategy: 'plain',
    supportsInteractionSettings: true,
    defaultRequireInteraction: false,
    defaultInteractionMessage: 'Select a region on the map to continue.',
  },
  categorization: {
    label: 'Categorization',
    chooserLabel: 'Categorization',
    completionMode: 'none',
    titleStrategy: 'plain',
    defaultRequireInteraction: true,
    alwaysRequiresInteraction: true,
  },
  'single-choice-answer': {
    label: 'Single Choice Answer',
    chooserLabel: 'Single Choice Answer',
    completionMode: 'none',
    titleStrategy: 'plain',
    defaultRequireInteraction: true,
    alwaysRequiresInteraction: true,
    supportsHideResults: true,
  },
  'freeform-text': {
    label: 'Freeform Text',
    chooserLabel: 'Freeform Text',
    completionMode: 'none',
    titleStrategy: 'plain',
    defaultRequireInteraction: true,
    alwaysRequiresInteraction: true,
    // No supportsInteractionSettings/supportsSuccessAnimationSettings (same
    // as likert-scale) - the only Block Settings entry is the forced-checked
    // "Hide results" lock, via forcesHideResults below - not the normal
    // optional supportsHideResults toggle.
    forcesHideResults: true,
  },
  'lesson-complete': {
    label: 'Lesson Complete',
    chooserLabel: 'Lesson Complete',
    completionMode: 'scroll',
    titleStrategy: 'plain',
  },
  link: {
    label: 'Link',
    chooserLabel: 'Link',
    completionMode: 'none',
    titleStrategy: 'plain',
    supportsInteractionSettings: true,
    defaultRequireInteraction: true,
    defaultInteractionMessage: 'You must open this link to continue.',
  },
};

export const MAIN_MENU_BLOCK_TYPES: readonly AuthoringBlockType[] = [
  'basic-content-a',
  'video',
  'stylized-numbered-list',
  'click-expand',
  'stylized-sidebar-callout',
  'stylized-text-callout',
  'comparison-slider',
  'policy-acknowledgement',
  'map',
  'electronic-signature',
  'link',
];

export const QUESTION_BLOCK_TYPES: readonly AuthoringBlockType[] = [
  'likert-scale',
  'single-choice-answer',
  'workplace-color-spectrum',
  'categorization',
  'freeform-text',
];

const AUTHORING_BLOCK_TYPES: readonly AuthoringBlockType[] = [
  ...MAIN_MENU_BLOCK_TYPES,
  ...QUESTION_BLOCK_TYPES,
  'lesson-complete',
];

export const AUTHORING_BLOCK_TYPE_OPTIONS: readonly AuthoringBlockTypeOption[] = AUTHORING_BLOCK_TYPES.map(
  (type) => ({
    type,
    chooserLabel: BLOCK_TYPE_METADATA[type].chooserLabel ?? BLOCK_TYPE_METADATA[type].label,
  })
);

type BlockTypeLabelInput = Pick<CourseBlock, 'type'> & {
  listStyle?: ListStyle;
};

export function blockTypeSupportsSettings(blockType: BlockType): boolean {
  const metadata = BLOCK_TYPE_METADATA[blockType] as BlockTypeMetadata | undefined;
  if (!metadata) return false;
  return metadata.supportsInteractionSettings === true
    || metadata.supportsSuccessAnimationSettings === true
    || metadata.supportsHideResults === true
    || metadata.forcesHideResults === true;
}

export function blockTypeForcesHideResults(blockType: BlockType): boolean {
  return BLOCK_TYPE_METADATA[blockType]?.forcesHideResults === true;
}

export function blockTypeSupportsInteractionSettings(blockType: BlockType): boolean {
  return BLOCK_TYPE_METADATA[blockType]?.supportsInteractionSettings === true;
}

export function blockTypeSupportsSuccessAnimationSettings(blockType: BlockType): boolean {
  return BLOCK_TYPE_METADATA[blockType]?.supportsSuccessAnimationSettings === true;
}

export function blockTypeSupportsHideResults(blockType: BlockType): boolean {
  return BLOCK_TYPE_METADATA[blockType]?.supportsHideResults === true;
}

export function blockTypeAlwaysRequiresInteraction(blockType: BlockType): boolean {
  return BLOCK_TYPE_METADATA[blockType]?.alwaysRequiresInteraction === true;
}

export function getBlockDefaultRequireInteraction(blockType: BlockType): boolean | undefined {
  const metadata = BLOCK_TYPE_METADATA[blockType] as BlockTypeMetadata | undefined;
  return metadata ? metadata.defaultRequireInteraction : false;
}

export function getBlockDefaultInteractionMessage(blockType: BlockType): string | undefined {
  const metadata = BLOCK_TYPE_METADATA[blockType] as BlockTypeMetadata | undefined;
  return metadata ? metadata.defaultInteractionMessage : '';
}

export function blockRequiresInteraction(
  block: Pick<CourseBlock, 'type'> & { requireInteraction?: boolean }
): boolean {
  return blockTypeAlwaysRequiresInteraction(block.type) || block.requireInteraction === true;
}

export function getBlockCompletionMode(
  block: Pick<CourseBlock, 'type'> & { requireInteraction?: boolean }
): BlockCompletionMode {
  if (blockRequiresInteraction(block)) {
    return 'interaction';
  }

  return BLOCK_TYPE_METADATA[block.type]?.completionMode ?? 'none';
}

/**
 * Account-aware variant of {@link blockRequiresInteraction}: the account's
 * "Require full videos" setting (accounts.enforceFullVideoView, NA-70) forces
 * video blocks to require completion, trumping the block author's own
 * requireInteraction toggle.
 */
export function blockRequiresCompletion(
  block: Pick<CourseBlock, 'type'> & { requireInteraction?: boolean },
  enforceFullVideoView: boolean
): boolean {
  return (
    blockRequiresInteraction(block) ||
    (block.type === 'video' && enforceFullVideoView)
  );
}

/**
 * Account-aware variant of {@link getBlockCompletionMode}; see
 * {@link blockRequiresCompletion}.
 */
export function getEffectiveBlockCompletionMode(
  block: Pick<CourseBlock, 'type'> & { requireInteraction?: boolean },
  enforceFullVideoView: boolean
): BlockCompletionMode {
  if (blockRequiresCompletion(block, enforceFullVideoView)) {
    return 'interaction';
  }

  return getBlockCompletionMode(block);
}

export function getBlockTitleStrategy(blockType: BlockType): BlockTitleStrategy {
  const meta = BLOCK_TYPE_METADATA[blockType] as BlockTypeMetadata | undefined;
  return meta?.titleStrategy ?? 'none';
}

export function isLessonCompleteBlock(block: Pick<CourseBlock, 'type'>): boolean {
  return block.type === 'lesson-complete';
}

export function getCourseBlockTypeLabel(block: BlockTypeLabelInput): string {
  if (block.type === 'stylized-numbered-list' && block.listStyle === 'bulleted') {
    return 'Bulleted List';
  }

  const meta = BLOCK_TYPE_METADATA[block.type as BlockType] as BlockTypeMetadata | undefined;
  return meta?.label ?? block.type;
}
